import { Divisor, useDivisor } from '../components/Divisor';
import { Icon } from '@iconify/react';
import { useState } from 'react';

import { Barra, Card, Carregando, Kpi, Ponto, Segmented, Tag, TagComPonto, TituloTela } from '../components/Sga';
import { agruparConsecutivos } from '../lib/agrupar';
import { hhmm, hhmmss, n, pct, rotuloEstado, kindDoEstado, segundos, telefone } from '../lib/format';
import type { Fila, PainelAntiban, SerieFila } from '../lib/types';

type Visao = 'timeline' | 'tabela' | 'grafico';

export function Filas({
  fila,
  serie,
  antiban,
  aoRecarregar,
  periodo,
}: {
  fila: Fila | null;
  serie: SerieFila | null;
  antiban: PainelAntiban | null;
  aoRecarregar: () => void;
  periodo: string;
}) {
  // A proporcao vira porcentagem da area: assim a divisoria acompanha o
  // redimensionamento da janela em vez de ficar presa a uma largura em pixels.
  const divisor = useDivisor({
    chave: 'filas',
    inicial: 59,
    min: 35,
    max: 78,
    passo: 2,
    calcular: (clientX, area) => ((clientX - area.left) / area.width) * 100,
    rotulo: 'Divisória entre a fila e o Anti-Ban',
  });

  const [visao, setVisao] = useState<Visao>('timeline');

  if (!fila || !antiban) return <Carregando texto="Levantando multifilas…" />;

  // Um lote de 25 boletos gera 25 linhas com o mesmo texto. Agrupa vizinhos
  // com mesmo conteúdo, conexão, estado e motivo — a lista fica navegável e o
  // volume aparece no selo.
  const itensAgrupados = agruparConsecutivos(
    fila.itens,
    (i) => `${i.titulo}|${i.conexao_id}|${i.estado}|${i.antiban_reason ?? ''}`,
    (i) => i.hora,
  );

  const riscoKind =
    antiban.resumo.risco_rotulo === 'Crítico'
      ? 'err'
      : antiban.resumo.risco_rotulo === 'Atenção'
        ? 'warn'
        : 'ok';
  const throttlingAtivo = antiban.throttling.some((l) => l.estado === 'Limitando' || l.estado === 'Em pausa');

  return (
    <div className="tela-fixa">
      <TituloTela
        icone="ant-design:cluster-outlined"
        titulo="Multifilas e Anti-Ban"
        contexto={periodo}
        acoes={
          <>
            <Segmented
              valor={visao}
              onChange={setVisao}
              opcoes={[
                { valor: 'timeline', rotulo: 'Timeline' },
                { valor: 'tabela', rotulo: 'Tabela' },
                { valor: 'grafico', rotulo: 'Gráfico' },
              ]}
            />
            <button type="button" className="btn btn-default btn-sm" onClick={aoRecarregar}>
              <Icon icon="mdi:refresh" width={15} /> Atualizar
            </button>
          </>
        }
      />

      <div
        className="sga-grid sga-grid--filas tela-fixa__corpo"
        ref={divisor.areaRef}
        style={{ gridTemplateColumns: `minmax(0, ${divisor.valor}fr) auto minmax(0, ${100 - divisor.valor}fr)` }}
      >
        <Card
          className="card-fixo"
          titulo="Fila do tenant"
          acoes={throttlingAtivo ? <Tag kind="warn">Throttling ativo</Tag> : <Tag>Fluxo normal</Tag>}
        >
          {/* Faixa de metricas: parada, junto com o cabecalho. */}
          <div
            className="sga-grid card-fixo__topo"
            style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: 14 }}
          >
            <Kpi icone="mdi:timer-sand" rotulo="Aguardando" valor={n(fila.resumo.aguardando)} />
            <Kpi
              icone="mdi:shield-alert-outline"
              rotulo="Retidas"
              valor={n(fila.resumo.retidas)}
              cor="var(--sga-warning)"
            />
            <Kpi icone="mdi:speedometer-slow" rotulo="Throughput" valor={`${n(fila.resumo.throughput_hora)}/h`} />
            <Kpi
              icone="mdi:timer-sand"
              rotulo="Espera média"
              valor={segundos(fila.resumo.espera_media_s * 1000)}
            />
            <Kpi
              icone="mdi:close-octagon-outline"
              rotulo="Falhas 24h"
              valor={n(fila.resumo.falhas_24h)}
              cor={fila.resumo.falhas_24h > 0 ? 'var(--sga-error)' : undefined}
            />
          </div>

          {/* Daqui para baixo rola. */}
          <div className="card-fixo__rolavel">
          {fila.itens.length === 0 && (
            <div className="sga-vazio">
              <Icon icon="mdi:check-circle-outline" width={24} />
              Fila vazia — nada retido no momento.
            </div>
          )}

          {visao === 'timeline' && fila.itens.length > 0 && (
            <div className="sga-timeline">
              {itensAgrupados.map(({ item, vezes }) => {
                const kind = kindDoEstado(item.estado);
                return (
                  <div key={item.id} style={{ display: 'contents' }}>
                    <div className="sga-timeline__hora">{hhmmss(item.hora)}</div>
                    <div className="sga-timeline__trilho">
                      <Ponto kind={kind} />
                      <i />
                    </div>
                    <div
                      className={`sga-timeline__card${
                        kind === 'warn' ? ' sga-timeline__card--warn' : kind === 'err' ? ' sga-timeline__card--err' : ''
                      }`}
                    >
                      <div className="sga-timeline__titulo">
                        <span style={{ flex: 1, minWidth: 0 }}>{item.titulo}</span>
                        {vezes > 1 && (
                          <span className="sga-repeticao" title={`${vezes} mensagens iguais em sequência`}>
                            ×{vezes}
                          </span>
                        )}
                        <Tag kind={kind}>{rotuloEstado[item.estado]}</Tag>
                      </div>
                      <div className="sga-timeline__meta">
                        {item.conexao} · {telefone(item.destino)} · {item.origem}
                        {item.tentativas > 1 ? ` · ${item.tentativas} tentativas` : ''}
                      </div>
                      {item.antiban_reason === 'split' && (
                        <div className="sga-timeline__antiban">
                          <Icon icon="mdi:drag-horizontal-variant" width={15} />
                          Anti-Ban: lote fracionado em {item.batch_total ?? '—'} destinatários — envio em janelas
                        </div>
                      )}
                      {item.antiban_reason && item.antiban_reason !== 'split' && (
                        <div className="sga-timeline__antiban">
                          <Icon icon="mdi:timer-sand" width={15} />
                          {textoAntiban(item.antiban_reason, item.delay_ms)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {visao === 'tabela' && fila.itens.length > 0 && (
            <div className="sga-tabela-scroll">
              <table className="sga-table sga-table--densa">
                <thead>
                  <tr>
                    <th>Hora</th>
                    <th>Mensagem</th>
                    <th>Conexão</th>
                    <th>Destino</th>
                    <th>Origem</th>
                    <th style={{ textAlign: 'right' }}>Delay aplicado</th>
                    <th style={{ textAlign: 'right' }}>Tentativas</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {fila.itens.map((i) => (
                    <tr key={i.id}>
                      <td className="mono">{hhmmss(i.hora)}</td>
                      <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.titulo}</td>
                      <td>{i.conexao}</td>
                      <td className="mono">{telefone(i.destino)}</td>
                      <td>{i.origem}</td>
                      <td className="num">{segundos(i.delay_ms)}</td>
                      <td className="num">{i.tentativas}</td>
                      <td>
                        <Tag kind={kindDoEstado(i.estado)}>{rotuloEstado[i.estado]}</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {visao === 'grafico' && <Grafico serie={serie} />}
          </div>
        </Card>

        <Divisor {...divisor.props} />

        <div className="coluna-rolavel" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Card
            titulo="Anti-Ban"
            acoes={
              <Tag>
                Ativo em {antiban.resumo.ativo_em} de {antiban.resumo.de_total} conexões
              </Tag>
            }
          >
            <div style={{ fontSize: 12, color: 'var(--sga-fg-2)' }}>Risco de banimento</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '2px 0 10px' }}>
              <strong style={{ fontSize: 26, fontWeight: 400 }}>{antiban.resumo.risco_rotulo}</strong>
              <span className="mono" style={{ color: 'var(--sga-fg-3)', fontSize: 12.5 }}>
                {pct(antiban.resumo.risco_pct)}
              </span>
            </div>
            <div className="sga-medidor">
              <div
                style={{
                  width: `${antiban.resumo.risco_pct}%`,
                  height: '100%',
                  borderRadius: 999,
                  background:
                    riscoKind === 'err'
                      ? 'var(--sga-error)'
                      : riscoKind === 'warn'
                        ? 'var(--sga-warning)'
                        : 'var(--sga-tag-text)',
                }}
              />
            </div>
            <div className="sga-medidor__escala">
              <span>seguro</span>
              <span>atenção</span>
              <span>crítico</span>
            </div>

            <div style={{ marginTop: 14 }}>
              {antiban.regras
                .filter((r) => r.kind !== 'nao_aplica')
                .map((r) => (
                  <div key={r.id}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, margin: '10px 0 4px' }}>
                      Perfil {r.nome}
                    </div>
                    <Regra rotulo="Intervalo entre envios" valor={`${segundos(r.min_interval_ms)}–${segundos(r.max_interval_ms)}`} />
                    <Regra rotulo="Limite por hora" valor={r.hourly_limit ? `${r.hourly_limit} msg/h` : 'sem limite'} />
                    <Regra
                      rotulo="Pausa periódica"
                      valor={
                        r.pause_every
                          ? `${Math.round(r.pause_duration_s / 60)} min a cada ${r.pause_every}`
                          : '—'
                      }
                    />
                    <Regra rotulo="Aquecimento" valor={r.warmup_days ? `${r.warmup_days} dias` : '—'} />
                    <Regra rotulo="Fracionamento" valor={`${r.batch_size} por janela · ${r.batch_gap_s} s`} />
                  </div>
                ))}
            </div>
          </Card>

          <Card titulo="Throttling por conexão">
            {antiban.throttling.map((l) => {
              const kind =
                l.estado === 'Banido'
                  ? 'err'
                  : l.estado === 'Limitando' || l.estado === 'Em pausa'
                    ? 'warn'
                    : l.estado === 'Sessão caiu'
                      ? 'mut'
                      : 'ok';
              return (
                <div key={l.conexao_id} style={{ padding: '9px 0', borderBottom: '1px solid var(--sga-border-gray)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Ponto kind={kind} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>{l.nome}</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {l.taxa}
                    </span>
                    <TagComPonto kind={kind}>{l.estado}</TagComPonto>
                  </div>
                  <div style={{ margin: '6px 0 4px' }}>
                    <Barra pct={l.pct} kind={kind} altura={6} />
                  </div>
                  <div style={{ display: 'flex', fontSize: 11.5, color: 'var(--sga-fg-3)' }}>
                    <span>{l.detalhe}</span>
                    <span style={{ flex: 1 }} />
                    <span>fila {n(l.na_fila)}</span>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Texto da faixa laranja da timeline, por motivo do Anti-Ban. */
function textoAntiban(motivo: string, delayMs: number): string {
  const quanto = delayMs > 0 ? ` de ${segundos(delayMs)}` : '';
  switch (motivo) {
    case 'pause':
      return `Anti-Ban: conexão em pausa programada${quanto ? ` — retoma em${quanto}` : ''}`;
    case 'rate_limit':
      return 'Anti-Ban: limite de envios por hora atingido — retoma na próxima janela';
    case 'sessao_indisponivel':
      return 'Sessão indisponível — a mensagem volta para a fila quando o número reconectar';
    default:
      return `Anti-Ban: atraso intencional${quanto} antes do envio`;
  }
}

function Regra({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="sga-linha-metrica">
      <span style={{ fontSize: 12.5 }}>{rotulo}</span>
      <span className="sga-linha-metrica__valor">{valor}</span>
    </div>
  );
}

function Grafico({ serie }: { serie: SerieFila | null }) {
  if (!serie) return <Carregando texto="Montando série horária…" />;

  const pico = Math.max(1, ...serie.serie.map((p) => Math.max(p.enviadas, p.retidas)));

  return (
    <>
      <div className="sga-legenda" style={{ marginBottom: 4 }}>
        <span>
          <i style={{ background: 'var(--sga-primary)' }} />
          Enviadas
        </span>
        <span>
          <i style={{ background: 'var(--sga-warning)' }} />
          Retidas pelo Anti-Ban
        </span>
      </div>
      <div className="sga-grafico">
        {serie.serie.map((p) => (
          <div className="sga-grafico__par" key={p.hora}>
            <span
              style={{ height: `${(p.enviadas / pico) * 100}%`, background: 'var(--sga-primary)' }}
              title={`${n(p.enviadas)} enviadas`}
            />
            <span
              style={{ height: `${(p.retidas / pico) * 100}%`, background: 'var(--sga-warning)' }}
              title={`${n(p.retidas)} retidas`}
            />
          </div>
        ))}
      </div>
      <div className="sga-grafico__eixo">
        {serie.serie.map((p) => (
          <span key={p.hora}>{hhmm(p.hora)}</span>
        ))}
      </div>
      <div className="sga-grid sga-grid--dash" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 12 }}>
        <Kpi
          icone="mdi:speedometer-slow"
          rotulo="Pico"
          valor={n(serie.resumo.pico)}
          nota={serie.resumo.pico_hora ? `às ${hhmm(serie.resumo.pico_hora)}` : '—'}
        />
        <Kpi
          icone="mdi:shield-alert-outline"
          rotulo="Retidas"
          valor={pct(serie.resumo.retidas_pct)}
          cor="var(--sga-warning)"
        />
        <Kpi
          icone="mdi:timer-sand"
          rotulo="Janela"
          valor={`${serie.serie.length} h`}
          nota="série por hora"
        />
      </div>
    </>
  );
}
